import React from 'react';

import { cssMap } from '@atlaskit/css';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { ReactRenderer } from '@atlaskit/renderer';
import { token } from '@atlaskit/tokens';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';

const styles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.0'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.small'),
		justifyContent: 'center',
	},
	summary: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.100'),
		paddingTop: token('space.150'),
		paddingRight: token('space.250'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.250'),
	},
	icon: {
		display: 'inline-flex',
		color: token('color.text.subtle'),
	},
	divider: {
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
	},
	content: {
		paddingTop: token('space.150'),
		paddingRight: token('space.250'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.250'),
	},
});

export type AdditionalNoteBaseProps = {
	icon: React.ReactNode;
	title: string;
	description?: string;
	testId: string;
};

export const AdditionalNoteBase = ({
	icon,
	title,
	description,
	testId,
}: AdditionalNoteBaseProps): React.JSX.Element | null => {
	if (!description) {
		return null;
	}

	return (
		<Stack xcss={styles.container} testId={testId}>
			<Inline xcss={styles.summary}>
				<Inline xcss={styles.icon}>{icon}</Inline>
				<Text>{title}</Text>
			</Inline>
			<Box xcss={styles.divider} />
			<Box xcss={styles.content}>
				<SmartCardProvider>
					<ReactRenderer document={encodeAdf(description)} />
				</SmartCardProvider>
			</Box>
		</Stack>
	);
};
